// src/pages/Produtos.js
import React, { useContext, useState, useEffect, useMemo } from 'react';
import { ProductContext } from '../store/productContext';
import { AuthContext } from '../store/authContext';

// Componentes originais
import ProductForm from '../components/Products/ProductForm';
import ProductDetail from '../components/Products/ProductDetail';
import ProductCampaignForm from '../components/Products/ProductCampaignForm';
import AccessDenied from '../components/UI/AccessDenied';

// Componentes de visualização
import {
  KpiCardGroup,
  DataWidget,
  InsightGenerator,
  MetricTable
} from '../components/Visualization';

// Hooks de dados
import useVisualizationData from '../hooks/useVisualizationData';

// Ícones
import {
  ShoppingBag, Plus, ChevronLeft, DollarSign, TrendingUp, BarChart2,
  Tag, Award, FileText, Search, Activity, RefreshCw, Calendar, Edit,
  Trash2, Eye, PieChart
} from 'lucide-react';

const Produtos = () => {
  const [activeTab, setActiveTab] = useState('products');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isAddingCampaign, setIsAddingCampaign] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [timeRange, setTimeRange] = useState('30d');
  const [showInsights, setShowInsights] = useState(true);
  const [viewMode, setViewMode] = useState('grid');

  const {
    products, addProduct, removeProduct, updateProduct,
    addProductCampaign, removeProductCampaign,
    getProductCampaigns, getProductMetrics, getOverallProductMetrics
  } = useContext(ProductContext);

  const { hasPermission } = useContext(AuthContext);
  const canViewProducts = hasPermission('view_products');
  const canEditProducts = hasPermission('edit_products');

  const { data: productData, insights: productInsights, refresh: refreshProductData } = useVisualizationData({
    dataType: 'products',
    timeRange,
    metrics: [
      { key: 'revenue', label: 'Receita', aggregation: 'sum' },
      { key: 'cost', label: 'Custo', aggregation: 'sum' },
      { key: 'profit', label: 'Lucro', aggregation: 'sum', detectAnomalies: true },
      { key: 'roi', label: 'ROI', aggregation: 'avg', detectAnomalies: true },
      { key: 'sales', label: 'Vendas', aggregation: 'sum' }
    ],
    generateInsights: true,
    detectAnomalies: true
  });

  const { data: salesTimelineData } = useVisualizationData({
    dataType: 'products',
    timeRange,
    metrics: [
      { key: 'sales', label: 'Vendas', aggregation: 'sum' },
      { key: 'revenue', label: 'Receita', aggregation: 'sum' }
    ],
    groupBy: 'product,date'
  });

  const { data: funnelData } = useVisualizationData({
    dataType: 'products',
    timeRange,
    metrics: [
      { key: 'views', label: 'Visualizações', aggregation: 'sum' },
      { key: 'clicks', label: 'Cliques', aggregation: 'sum' },
      { key: 'checkouts', label: 'Checkouts', aggregation: 'sum' },
      { key: 'sales', label: 'Vendas', aggregation: 'sum' }
    ]
  });

  const [overallMetrics, setOverallMetrics] = useState({});
  useEffect(() => {
    if (products.length > 0) {
      setOverallMetrics(getOverallProductMetrics());
    }
  }, [products, getOverallProductMetrics]);

  const selectedProductPerformance = useMemo(() => {
    return selectedProduct ? salesTimelineData.filter(item => item.product === selectedProduct.id) : [];
  }, [salesTimelineData, selectedProduct]);

  const handleSelectProduct = (product) => {
    setSelectedProduct(product);
    setActiveTab('detail');
    setIsAddingCampaign(false);
  };

  if (!canViewProducts) return <AccessDenied message="Você não tem permissão para visualizar os produtos." />;

  return (
  <div className="page-container px-4 sm:px-6 lg:px-8">
    <h1 className="page-title mb-1">Gestão de Produtos</h1>
    
    {/* Renderizar quando NÃO há produto selecionado */}
    {!selectedProduct && (
      <>
        {/* KPIs e métricas gerais */}
        <KpiCardGroup 
          metrics={[
            {
              title: 'Total de Produtos',
              value: products.length,
              icon: <ShoppingBag className="h-5 w-5 text-blue-600" />
            },
            {
              title: 'Receita Total',
              value: overallMetrics.totalRevenue || 0,
              prefix: 'R$ ',
              icon: <DollarSign className="h-5 w-5 text-green-600" />
            },
            // Adicione outros KPIs aqui
          ]}
        />
        
        {/* Formulário de produto */}
        {canEditProducts && (
          <div className="card mt-6">
            <div className="card-header">
              <h2 className="card-title m-0">
                {editingProduct ? 'Editar Produto' : 'Adicionar Produto'}
              </h2>
            </div>
            <div className="p-6">
              <ProductForm 
                onAddProduct={addProduct}
                editingProduct={editingProduct}
                // Adicione outras props necessárias
              />
            </div>
          </div>
        )}
        
        {/* Lista de produtos */}
        <div className="card mt-6">
          <div className="card-header">
            <h2 className="card-title m-0">Lista de Produtos</h2>
          </div>
          <div className="p-0">
            {/* Aqui use o componente ProductList que você importou */}
            <MetricTable 
              data={products}
              columns={[
                { key: 'name', label: 'Nome' },
                { key: 'price', label: 'Preço', type: 'number', format: v => `R$ ${v}` },
                // Adicione outras colunas conforme necessário
              ]}
              // Adicione outras props necessárias
              onRowClick={handleSelectProduct}
            />
          </div>
        </div>
      </>
    )}

      {selectedProduct && (
        <>
          <div className="card overflow-hidden min-w-0">
            <div className="card-header">
              <h2 className="card-title m-0 flex items-center">
                <ShoppingBag className="h-5 w-5 mr-2 text-blue-600" />
                {selectedProduct.name}
              </h2>
              <div className="flex space-x-3">
                <button onClick={() => setSelectedProduct(null)} className="btn-outline text-sm flex items-center">
                  <ChevronLeft className="h-4 w-4 mr-2" /> Voltar para Lista
                </button>
              </div>
            </div>
            <ProductDetail
              product={selectedProduct}
              campaigns={getProductCampaigns(selectedProduct.id)}
              metrics={getProductMetrics(selectedProduct.id)}
            />
          </div>

          {selectedProductPerformance.length > 0 && (
            <div className="mt-6">
              <DataWidget
                title={`Performance de ${selectedProduct.name}`}
                type="chart"
                config={{
                  data: selectedProductPerformance,
                  series: [
                    { type: 'line', dataKey: 'sales', name: 'Vendas', color: '#3B82F6' },
                    { type: 'bar', dataKey: 'revenue', name: 'Receita', color: '#10B981' }
                  ],
                  xAxisDataKey: 'date',
                  height: 300
                }}
                data={selectedProductPerformance}
                height={300}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Produtos;
